import { SyncMessageType, SyncResponseMessageType } from "@taskfortress/core"
import type {
  SyncMessageDataMap,
  SyncRegisteredResponse,
} from "@taskfortress/core"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import type { ReactNode } from "react"
import appConfig from "../../app.config"
import { useAuth } from "../auth/AuthProvider"
import { createLogger } from "../lib/logger"

// Create a logger instance for the SocketProvider
const logger = createLogger("socket")

export enum SocketConnectionStatus {
  CONNECTING = "connecting",
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  REGISTERED = "registered",
}

export interface SocketContextType {
  connectionStatus: SocketConnectionStatus
  send: <T extends SyncMessageType>(
    type: T,
    data: Omit<SyncMessageDataMap[T], "type">,
  ) => void
  addMessageListener: (listener: (event: MessageEvent) => void) => void
  removeMessageListener: (listener: (event: MessageEvent) => void) => void
  addErrorHandler: (handler: (error: any) => void) => void
  removeErrorHandler: (handler: (error: any) => void) => void
}

const SocketContext = createContext<SocketContextType | null>(null)

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider")
  }
  return context
}

interface SocketProviderProps {
  children: ReactNode
}

// Helper function to send a message to the WebSocket
const sendToSocket = <T extends SyncMessageType>(
  ws: WebSocket,
  type: T,
  data: Omit<SyncMessageDataMap[T], "type">,
) => {
  ws.send(JSON.stringify({ type, ...data }))
}

export const SocketProvider = ({ children }: SocketProviderProps) => {
  const [socket, setSocket] = useState<WebSocket | null>(null)
  const [connectionStatus, setConnectionStatus] =
    useState<SocketConnectionStatus>(SocketConnectionStatus.DISCONNECTED)
  const { token, isAuthenticated } = useAuth()
  const messageListeners = useRef<Set<(event: MessageEvent) => void>>(new Set())
  const errorHandlers = useRef<Set<(error: any) => void>>(new Set())
  const registrationPromise = useRef<Promise<void> | null>(null)
  const pendingMessages = useRef<Array<{ type: SyncMessageType; data: any }>>(
    [],
  )
  const resolveRegistration = useRef<() => void>(() => {
    // Empty resolver placeholder, will be replaced when promise is created
  })

  // Connect to WebSocket when auth state changes
  useEffect(() => {
    let ws: WebSocket | null = null
    let reconnectTimeout: number | null = null

    const connect = () => {
      try {
        // Clear any existing reconnect timeouts
        if (reconnectTimeout) {
          window.clearTimeout(reconnectTimeout)
          reconnectTimeout = null
        }

        if (!isAuthenticated || !token) {
          setConnectionStatus(SocketConnectionStatus.DISCONNECTED)
          return
        }

        setConnectionStatus(SocketConnectionStatus.CONNECTING)
        logger.debug("Connecting to server")

        // Reset registration promise
        registrationPromise.current = new Promise<void>((resolve) => {
          resolveRegistration.current = resolve
        })

        // Get server URL from window location
        const wsUrl = `${appConfig.extra.apiUrl}/ws`

        // Create new WebSocket connection
        ws = new WebSocket(wsUrl)

        ws.onopen = () => {
          setConnectionStatus(SocketConnectionStatus.CONNECTED)
          logger.debug("Connected to server")

          // Register with the server, including JWT token
          if (ws) {
            logger.debug("Sending register message")
            sendToSocket(ws, SyncMessageType.REGISTER, { jwtToken: token })
          }
        }

        ws.onclose = () => {
          setConnectionStatus(SocketConnectionStatus.DISCONNECTED)
          logger.debug("Disconnected from server")

          // Reset registration promise on disconnect
          registrationPromise.current = null

          // Reconnect after a delay
          reconnectTimeout = window.setTimeout(() => {
            if (isAuthenticated && token) {
              connect()
            }
          }, 3000)
        }

        ws.onerror = () => {
          setConnectionStatus(SocketConnectionStatus.DISCONNECTED)
          // Reset registration promise on error
          registrationPromise.current = null
        }

        // Handle messages from the server
        ws.onmessage = (event: MessageEvent) => {
          try {
            const message = JSON.parse(event.data as string)
            logger.debug("Received socket message", { message })

            // Handle registration confirmation
            if (message.type === SyncResponseMessageType.REGISTERED) {
              setConnectionStatus(SocketConnectionStatus.REGISTERED)
              logger.debug("Registered with server", {
                userId: (message as SyncRegisteredResponse).data.userId,
              })
              resolveRegistration.current()

              // Send any messages that were queued while waiting for registration
              if (ws && pendingMessages.current.length > 0) {
                logger.debug(
                  `Sending ${pendingMessages.current.length} pending messages`,
                )
                pendingMessages.current.forEach(({ type, data }) => {
                  sendToSocket(ws!, type, data)
                })
                pendingMessages.current = []
              }
            }

            // Handle error messages from the server
            if (message.type === SyncResponseMessageType.ERROR) {
              errorHandlers.current.forEach((handler) => {
                try {
                  handler(message.data || {})
                } catch (err) {
                  logger.error("Error in error handler", { err })
                }
              })
            }

            // Notify all message listeners
            messageListeners.current.forEach((listener) => {
              try {
                listener(event)
              } catch (err) {
                logger.error("Error in message listener", { err })
              }
            })
          } catch (err) {
            logger.error("Failed to parse WebSocket message", { err })
          }
        }

        setSocket(ws)
      } catch (error) {
        logger.error("Failed to connect to WebSocket", { error })
        setConnectionStatus(SocketConnectionStatus.DISCONNECTED)
        registrationPromise.current = null

        // Attempt to reconnect after a delay
        reconnectTimeout = window.setTimeout(() => {
          if (isAuthenticated && token) {
            connect()
          }
        }, 5000)
      }
    }

    connect()

    // Cleanup function
    return () => {
      if (reconnectTimeout) {
        window.clearTimeout(reconnectTimeout)
      }

      if (ws) {
        ws.close()
      }
      registrationPromise.current = null
    }
  }, [isAuthenticated, token])

  // Add message listener
  const addMessageListener = useCallback(
    (listener: (event: MessageEvent) => void) => {
      messageListeners.current.add(listener)
    },
    [],
  )

  // Remove message listener
  const removeMessageListener = useCallback(
    (listener: (event: MessageEvent) => void) => {
      messageListeners.current.delete(listener)
    },
    [],
  )

  // Add error handler
  const addErrorHandler = useCallback((handler: (error: any) => void) => {
    errorHandlers.current.add(handler)
  }, [])

  // Remove error handler
  const removeErrorHandler = useCallback((handler: (error: any) => void) => {
    errorHandlers.current.delete(handler)
  }, [])

  // Type-safe send message helper that waits for registration
  const send = useCallback<SocketContextType["send"]>(
    <T extends SyncMessageType>(
      type: T,
      data: Omit<SyncMessageDataMap[T], "type">,
    ) => {
      // If not connected, log warning and don't send
      if (!socket || connectionStatus === SocketConnectionStatus.DISCONNECTED) {
        logger.warn("Cannot send message, socket not connected", {
          type,
          connectionStatus,
        })
        return
      }

      // If registered, send immediately
      if (connectionStatus === SocketConnectionStatus.REGISTERED) {
        sendToSocket(socket, type, data)
        return
      }

      // If connecting or connected but not registered yet, queue the message
      logger.debug("Queuing message until registration is complete", { type })
      pendingMessages.current.push({ type, data: data as any })

      // If we're already connected, return the registration promise
      if (registrationPromise.current) {
        registrationPromise.current
          .then(() => {
            logger.debug("Registration complete, sending queued messages")
          })
          .catch((err) => {
            logger.error("Registration failed", { err })
          })
      }
    },
    [socket, connectionStatus],
  )

  return (
    <SocketContext.Provider
      value={{
        connectionStatus,
        send,
        addMessageListener,
        removeMessageListener,
        addErrorHandler,
        removeErrorHandler,
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}
